import {
  Box,
  Flex,
  Grid,
  GridItem,
  SimpleGrid,
  Skeleton,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { AngularNavLink } from '../../../shared/AngularNavLink';
import { PdBranchOverviewRow, PdOverviewStage } from './usePdOverview';

type StageData = PdOverviewStage;
type BranchOverviewRow = PdBranchOverviewRow;

export function OverviewHeader({
  isLoading,
  scopeLabel,
}: {
  isLoading: boolean;
  scopeLabel: string;
}) {
  const { t } = useTranslation();

  return (
    <Flex
      align={{ base: 'start', md: 'end' }}
      borderBottom="1px solid"
      borderColor="entityDirectory.border"
      direction={{ base: 'column', md: 'row' }}
      gap={5}
      justify="space-between"
      pb="22px"
    >
      <Flex direction="column" gap="7px" maxW="700px">
        <Text
          color="entityDirectory.primaryBackground"
          fontSize="11px"
          fontWeight="bold"
          letterSpacing="0.14em"
        >
          {t('productDevelopmentOverview.eyebrow')}
        </Text>
        <Text
          color="entityDirectory.text"
          fontFamily="barlow"
          fontSize={{ base: '30px', lg: '36px' }}
          fontWeight="extrabold"
          letterSpacing="-0.025em"
          lineHeight={{ base: '34px', lg: '40px' }}
        >
          {t('productDevelopmentOverview.title')}
        </Text>
        <Text
          color="entityDirectory.mutedText"
          fontSize="14px"
          lineHeight="20px"
        >
          {t('productDevelopmentOverview.description')}
        </Text>
      </Flex>
      <Flex
        align={{ base: 'start', md: 'end' }}
        direction="column"
        flexShrink={0}
        gap="5px"
      >
        <Text
          color="entityDirectory.mutedText"
          fontSize="11px"
          fontWeight="bold"
          letterSpacing="0.08em"
        >
          {t('productDevelopmentOverview.currentScope')}
        </Text>
        <Skeleton isLoaded={!isLoading} minW={isLoading ? '92px' : undefined}>
          <Text
            color="entityDirectory.text"
            fontSize="14px"
            fontWeight="semibold"
          >
            {scopeLabel}
          </Text>
        </Skeleton>
      </Flex>
    </Flex>
  );
}
export function DevelopmentFlow({
  isOnTrack,
  stages,
}: {
  isOnTrack: boolean;
  stages: StageData[];
}) {
  const { t } = useTranslation();
  const isLoading = stages.some((stage) => stage.loading);

  return (
    <Section borderColor="entityDirectory.text">
      <SectionHeader
        description={t('productDevelopmentOverview.flowDescription')}
        meta={
          <Skeleton isLoaded={!isLoading} minW={isLoading ? '76px' : undefined}>
            <Flex align="center" gap={2}>
              <Box
                bg={
                  isOnTrack
                    ? 'offerDrawer.success'
                    : 'entityDirectory.primaryBackground'
                }
                boxSize="7px"
              />
              <Text
                color={
                  isOnTrack
                    ? 'offerDrawer.success'
                    : 'entityDirectory.primaryBackground'
                }
                fontSize="12px"
                fontWeight="semibold"
              >
                {t(
                  isOnTrack
                    ? 'productDevelopmentOverview.onTrack'
                    : 'productDevelopmentOverview.inProgress',
                )}
              </Text>
            </Flex>
          </Skeleton>
        }
        title={t('productDevelopmentOverview.developmentFlow')}
      />
      <Box overflowX="auto">
        <SimpleGrid columns={stages.length} minW="900px">
          {stages.map((stage, index) => (
            <AngularNavLink
              key={stage.route}
              navobj={stage.navobj ?? {}}
              to={stage.route}
              variant="unstyled"
            >
              <Flex
                bg={
                  stage.tone === 'attention'
                    ? 'entityDirectory.raisedSurface'
                    : undefined
                }
                borderRight={index < stages.length - 1 ? '1px solid' : 0}
                borderColor="entityDirectory.border"
                direction="column"
                gap="5px"
                h="104px"
                px="18px"
                py="15px"
                _hover={{ bg: 'entityDirectory.raisedSurface' }}
              >
                <Text
                  color={
                    stage.tone === 'attention'
                      ? 'entityDirectory.primaryBackground'
                      : 'entityDirectory.mutedText'
                  }
                  fontSize="10px"
                  fontWeight="bold"
                  letterSpacing="0.11em"
                  lineHeight="13px"
                >
                  {String(index + 1).padStart(2, '0')} ·{' '}
                  {stage.label.toUpperCase()}
                </Text>
                <Skeleton
                  isLoaded={!stage.loading}
                  minW={stage.loading ? '34px' : undefined}
                >
                  <Text
                    color="entityDirectory.text"
                    fontFamily="barlow"
                    fontSize="26px"
                    fontWeight="extrabold"
                    lineHeight="28px"
                  >
                    {stage.count}
                  </Text>
                </Skeleton>
                {stage.description && (
                  <Skeleton
                    isLoaded={!stage.loading}
                    minW={stage.loading ? '110px' : undefined}
                  >
                    <Text
                      color={
                        stage.tone === 'attention'
                          ? 'entityDirectory.primaryBackground'
                          : 'entityDirectory.mutedText'
                      }
                      fontSize="12px"
                      fontWeight={
                        stage.tone === 'attention' ? 'semibold' : 'normal'
                      }
                      lineHeight="15px"
                    >
                      {stage.description}
                    </Text>
                  </Skeleton>
                )}
              </Flex>
            </AngularNavLink>
          ))}
        </SimpleGrid>
      </Box>
    </Section>
  );
}

export function BranchCoverage({
  currentBranchId,
  isLoading,
  rows,
}: {
  currentBranchId: number | null;
  isLoading: boolean;
  rows: BranchOverviewRow[];
}) {
  const { t } = useTranslation();

  return (
    <Section borderColor="entityDirectory.primaryBackground">
      <SectionHeader
        description={t('productDevelopmentOverview.branchDescription')}
        meta={
          <Skeleton isLoaded={!isLoading} minW={isLoading ? '92px' : undefined}>
            <Text
              color="entityDirectory.primaryBackground"
              fontSize="12px"
              fontWeight="semibold"
            >
              {t('productDevelopmentOverview.branchesShown', {
                count: rows.length,
              })}
            </Text>
          </Skeleton>
        }
        title={t('productDevelopmentOverview.branchCoverage')}
      />
      <Box overflowX="auto">
        <Grid
          bg="entityDirectory.raisedSurface"
          borderBottom="1px solid"
          borderColor="entityDirectory.border"
          minW="996px"
          templateColumns="170px 150px 150px 190px 160px 176px"
        >
          {[
            t('general.branch'),
            t('navigation.options'),
            t('navigation.suppliers'),
            t('navigation.versions'),
            t('navigation.inquiry'),
            t('navigation.offers'),
          ].map((label) => (
            <Text
              key={label}
              color="entityDirectory.mutedText"
              fontSize="10px"
              fontWeight="bold"
              letterSpacing="0.09em"
              lineHeight="13px"
              px={label === t('general.branch') ? '18px' : '14px'}
              py="11px"
            >
              {label.toUpperCase()}
            </Text>
          ))}
        </Grid>
        {isLoading ? (
          <BranchCoverageSkeleton />
        ) : rows.length === 0 ? (
          <Text
            color="entityDirectory.mutedText"
            fontSize="13px"
            minW="996px"
            px="18px"
            py={8}
            textAlign="center"
          >
            {t('productDevelopmentOverview.noBranchActivity')}
          </Text>
        ) : (
          rows.map((row) => (
            <BranchRow
              key={row.branch.branchId}
              isCurrentBranch={row.branch.branchId === currentBranchId}
              row={row}
            />
          ))
        )}
      </Box>
    </Section>
  );
}

function BranchCoverageSkeleton() {
  return (
    <>
      {[0, 1, 2].map((row) => (
        <Grid
          key={row}
          borderBottom="1px solid"
          borderColor="entityDirectory.border"
          minW="996px"
          templateColumns="170px 150px 150px 190px 160px 176px"
          _last={{ borderBottom: 0 }}
        >
          {[0, 1, 2, 3, 4, 5].map((column) => (
            <GridItem
              key={column}
              minH="64px"
              px={column === 0 ? '18px' : '14px'}
              py="12px"
            >
              <Skeleton h="14px" w={column === 0 ? '116px' : '72px'} />
              <Skeleton h="11px" mt="5px" w="88px" />
            </GridItem>
          ))}
        </Grid>
      ))}
    </>
  );
}

function BranchRow({
  isCurrentBranch,
  row,
}: {
  isCurrentBranch: boolean;
  row: BranchOverviewRow;
}) {
  const { t } = useTranslation();
  const supplierDescription =
    row.suppliers.length === 0
      ? t('productDevelopmentOverview.noneAssigned')
      : row.suppliers.length === 1
      ? row.suppliers[0]
      : t('productDevelopmentOverview.supplierAndMore', {
          supplier: row.suppliers[0],
          count: row.suppliers.length - 1,
        });
  const inquiryValue =
    row.inquiries.open > 0
      ? t('navigation.draftsCount', { count: row.inquiries.open })
      : row.inquiries.sent > 0
      ? t('productDevelopmentOverview.sentCount', {
          count: row.inquiries.sent,
        })
      : row.inquiries.accepted > 0
      ? t('productDevelopmentOverview.acceptedCount', {
          count: row.inquiries.accepted,
        })
      : t('productDevelopmentOverview.none');
  const inquiryDescription =
    row.inquiries.accepted > 0
      ? t('productDevelopmentOverview.acceptedCount', {
          count: row.inquiries.accepted,
        })
      : row.inquiries.sent > 0
      ? t('productDevelopmentOverview.awaitingReply')
      : t('productDevelopmentOverview.notStarted');
  const offerValue =
    row.offers.sent > 0
      ? t('productDevelopmentOverview.sentCount', { count: row.offers.sent })
      : row.offers.draft > 0
      ? t('navigation.draftsCount', { count: row.offers.draft })
      : t('productDevelopmentOverview.notStarted');
  const offerDescription =
    row.offers.draft > 0 && row.offers.totalPriceCount > 0
      ? t('productDevelopmentOverview.priceProgress', {
          offered: row.offers.offeredPriceCount,
          total: row.offers.totalPriceCount,
        })
      : row.offers.sent > 0
      ? t('productDevelopmentOverview.customerOfferSent')
      : row.inquiries.accepted > 0
      ? t('productDevelopmentOverview.acceptedPricesAvailable')
      : t('productDevelopmentOverview.acceptPricesFirst');

  return (
    <Grid
      borderBottom="1px solid"
      borderColor="entityDirectory.border"
      minW="996px"
      templateColumns="170px 150px 150px 190px 160px 176px"
      _last={{ borderBottom: 0 }}
    >
      <MetricCell
        description={
          isCurrentBranch
            ? t('productDevelopmentOverview.myBranch')
            : row.branch.branchName
        }
        value={`${row.branch.identifier} · ${row.branch.branchName}`}
      />
      <MetricCell
        description={t('productDevelopmentOverview.readyCount', {
          count: row.options.ready,
        })}
        descriptionTone={
          row.options.ready === row.options.total ? 'success' : undefined
        }
        route="variant.options"
        value={t('navigation.optionsCount', { count: row.options.total })}
      />
      <MetricCell
        description={supplierDescription}
        route="variant.suppliers"
        value={t('navigation.suppliersCount', {
          count: row.suppliers.length,
        })}
      />
      <MetricCell
        bg={row.versions === 0 ? 'entityDirectory.raisedSurface' : undefined}
        description={
          row.latestVersion === null
            ? t('productDevelopmentOverview.notStarted')
            : t('productDevelopmentOverview.latestVersion', {
                version: row.latestVersion,
              })
        }
        route="variant.versions"
        value={t('navigation.versionsCount', { count: row.versions })}
      />
      <MetricCell
        bg={
          row.inquiries.open > 0
            ? 'offerDrawer.dangerBackground'
            : row.inquiries.sent === 0 && row.inquiries.accepted === 0
            ? 'entityDirectory.raisedSurface'
            : undefined
        }
        description={inquiryDescription}
        navobj={{ branchId: row.branch.branchId, status: 'all' }}
        route="variant.inquiry"
        tone={
          row.inquiries.open > 0
            ? 'danger'
            : row.inquiries.accepted > 0
            ? 'success'
            : undefined
        }
        value={inquiryValue}
      />
      <MetricCell
        bg={
          row.offers.sent === 0 && row.offers.draft === 0
            ? 'entityDirectory.raisedSurface'
            : undefined
        }
        description={offerDescription}
        route="variant.offers"
        tone={row.offers.sent > 0 ? 'success' : undefined}
        value={offerValue}
      />
    </Grid>
  );
}

function MetricCell({
  bg,
  description,
  descriptionTone,
  navobj,
  route,
  tone,
  value,
}: {
  bg?: string;
  description: string;
  descriptionTone?: 'success';
  navobj?: Record<string, unknown>;
  route?: string;
  tone?: 'danger' | 'success';
  value: string;
}) {
  const toneColor =
    tone === 'danger'
      ? 'offerDrawer.danger'
      : tone === 'success'
      ? 'offerDrawer.success'
      : 'entityDirectory.text';

  const content = (
    <>
      <Text color={toneColor} fontSize="13px" fontWeight="semibold">
        {value}
      </Text>
      <Text
        color={
          descriptionTone === 'success'
            ? 'offerDrawer.success'
            : 'entityDirectory.mutedText'
        }
        fontSize="11px"
        lineHeight="14px"
        mt="2px"
        noOfLines={1}
      >
        {description}
      </Text>
    </>
  );

  if (!route) {
    return (
      <GridItem bg={bg} minH="64px" px="14px" py="12px" _first={{ px: '18px' }}>
        {content}
      </GridItem>
    );
  }

  return (
    <GridItem
      as={AngularNavLink}
      bg={bg}
      cursor="pointer"
      minH="64px"
      navobj={navobj ?? {}}
      px="14px"
      py="12px"
      to={route}
      transition="filter 120ms ease"
      variant="unstyled"
      _focusVisible={{
        outline: '2px solid',
        outlineColor: 'entityDirectory.primaryBackground',
        outlineOffset: '-2px',
      }}
      _hover={{ filter: 'brightness(0.9)' }}
    >
      {content}
    </GridItem>
  );
}

function Section({
  borderColor,
  children,
}: {
  borderColor: string;
  children: React.ReactNode;
}) {
  return (
    <Flex
      bg="entityDirectory.surface"
      borderTop="3px solid"
      borderColor={borderColor}
      direction="column"
    >
      {children}
    </Flex>
  );
}

function SectionHeader({
  description,
  meta,
  title,
}: {
  description: string;
  meta: React.ReactNode;
  title: string;
}) {
  return (
    <Flex
      align="center"
      borderBottom="1px solid"
      borderColor="entityDirectory.border"
      justify="space-between"
      px={5}
      py="15px"
    >
      <Flex direction="column" gap="3px">
        <Text
          color="entityDirectory.text"
          fontFamily="barlow"
          fontSize="18px"
          fontWeight="bold"
          lineHeight="22px"
        >
          {title}
        </Text>
        <Text color="entityDirectory.mutedText" fontSize="12px">
          {description}
        </Text>
      </Flex>
      {meta}
    </Flex>
  );
}
